import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box, Button, Card, CardContent, FormControl, InputLabel, MenuItem, Select, Typography, CircularProgress,
    Alert, TextField, Dialog, DialogTitle, DialogContent, IconButton, Tooltip,
    Accordion, AccordionSummary, AccordionDetails, Grid, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Search as SearchIcon } from '@mui/icons-material';

import {
    Product,
    AIService,
    PromptTemplate,
    FunnelGeneration,
    FunnelType,
    FunnelSlide,
    FunnelSlideRegenerateRequest,
    Marketplace,
} from '../types';

import {
    getProducts,
    fetchAIServices,
    getPromptTemplates,
    getFunnelGenerationsForProduct,
    getMarketplaces,
} from '../services/api';

// Импорты компонентов из папки funnel
import FunnelSlideViewer from '../components/funnel/FunnelSlideViewer';
import FunnelProgressIndicator from '../components/funnel/FunnelProgressIndicator';
import FunnelHistoryDialog from '../components/funnel/FunnelHistoryDialog';
import FunnelSlideRegenerateModal from '../components/funnel/FunnelSlideRegenerateModal';

import PromptViewerDialog from '../components/PromptViewerDialog';
import AIGuideDialog from '../components/AIGuideDialog';
import { useFunnelGeneration } from '../hooks/useFunnelGeneration';

const FUNNEL_TYPES: { value: FunnelType; label: string }[] = [
    { value: 'product_showcase', label: 'Product Showcase' },
    { value: 'storyline', label: 'Storyline' },
    { value: 'educational', label: 'Educational' },
    { value: 'comparison', label: 'Comparison' },
    { value: 'emotional_journey', label: 'Emotional Journey' },
];

const FunnelTestPage: React.FC = () => {
    // === UI States ===
    const [selectedReferenceImageUrl, setSelectedReferenceImageUrl] = useState<string | null>(null);
    const [selectedProductId, setSelectedProductId] = useState<number | ''>('');
    const [selectedAIServiceCode, setSelectedAIServiceCode] = useState<string>('');
    const [selectedSlideServiceCode, setSelectedSlideServiceCode] = useState<string>('');
    const [selectedPromptTemplateId, setSelectedPromptTemplateId] = useState<number | ''>('');
    const [slidesCount, setSlidesCount] = useState<number>(5);
    const [funnelType, setFunnelType] = useState<FunnelType>('product_showcase');
    const [targetMarketplace, setTargetMarketplace] = useState<string>('');
    const [language, setLanguage] = useState<string>('ru');
    const [additionalInstructions, setAdditionalInstructions] = useState<string>('');

    const [historyOpen, setHistoryOpen] = useState(false);
    const [fullscreenImageUrl, setFullscreenImageUrl] = useState<string | null>(null);
    const [isAIGuideOpen, setIsAIGuideOpen] = useState(false);
    const [viewedAIService, setViewedAIService] = useState<AIService | null>(null);
    const [isPromptViewerOpen, setIsPromptViewerOpen] = useState(false);
    const [viewedPromptTemplate, setViewedPromptTemplate] = useState<PromptTemplate | null>(null);
    const [promptsExpanded, setPromptsExpanded] = useState<boolean>(false);
    const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
    const [selectedSlideForRegeneration, setSelectedSlideForRegeneration] = useState<FunnelSlide | null>(null);

    // === API Queries ===
    const { data: products } = useQuery<Product[]>({
        queryKey: ['products'],
        queryFn: getProducts
    });

    const { data: marketplaces = [], isLoading: isLoadingMarketplaces } = useQuery<Marketplace[]>({
        queryKey: ['marketplaces'],
        queryFn: getMarketplaces
    });

    const { data: allAiServices } = useQuery<AIService[]>({
        queryKey: ['aiServices'],
        queryFn: () => fetchAIServices()
    });

    const { data: promptTemplates } = useQuery<PromptTemplate[]>({
        queryKey: ['promptTemplates', 'funnel_generation'],
        queryFn: () => getPromptTemplates('funnel_generation')
    });

    const { data: historyGenerations = [], isLoading: isLoadingHistory } = useQuery({
        queryKey: ['funnelGenerationHistory', selectedProductId],
        queryFn: () => getFunnelGenerationsForProduct(selectedProductId as number),
        enabled: !!selectedProductId,
    });

    // === Custom Hook ===
    const {
        generationStep,
        slidePrompts,
        generatedSlides,
        error,
        selectedPromptTemplate,
        selectedAIService,
        selectedSlideService,
        promptGenMutation,
        slideGenMutation,
        slideRegenerateMutation,
        resetGeneration,
        handleLoadGeneration,
        canStartPromptGen,
        canStartSlideGen,
        currentGeneration,
        // ✅ НОВЫЕ ФЛАГИ:
        canRetryFailedSlides,
        hasPartialSuccess,
        failedSlidesCount,
        completedSlidesCount,
        canRegeneratePrompts,
    } = useFunnelGeneration({
        selectedProductId,
        selectedAIServiceCode,
        selectedSlideServiceCode,
        selectedPromptTemplateId,
        slidesCount,
        funnelType,
        targetMarketplace,
        language,
        additionalInstructions,
        selectedReferenceImageUrl,
        allAiServices,
        promptTemplates,
    });

    // === Filtered Services ===
    const promptServices = allAiServices?.filter(s => s.supported_tasks?.includes('funnel_prompt_generation'));
    const slideServices = allAiServices?.filter(s => s.supported_tasks?.includes('slide_generation'));

    const selectedProduct = useMemo(() => {
        return products?.find(p => p.id === selectedProductId);
    }, [products, selectedProductId]);

    // === Event Handlers ===
    const resetAll = () => {
        setSelectedProductId('');
        setSelectedAIServiceCode('');
        setSelectedSlideServiceCode('');
        setSelectedPromptTemplateId('');
        setSlidesCount(5);
        setFunnelType('product_showcase');
        setTargetMarketplace('');
        setLanguage('ru');
        setAdditionalInstructions('');
        setSelectedReferenceImageUrl(null);
        resetGeneration();
    };

    const handleHistoryLoad = (gen: FunnelGeneration) => {
        setHistoryOpen(false);
        handleLoadGeneration(gen);
        setSelectedReferenceImageUrl(gen.reference_image_url);
        setSelectedProductId(gen.product_id);
        setSlidesCount(gen.slides_count);
        setFunnelType(gen.funnel_type);
        setTargetMarketplace(gen.target_marketplace);
    };

    const openFullscreenImage = (url: string) => {
        setFullscreenImageUrl(url);
    };

    const closeFullscreenImage = () => {
        setFullscreenImageUrl(null);
    };

    const handleViewAIService = (service: AIService | null) => {
        setViewedAIService(service);
        setIsAIGuideOpen(true);
    };

    const handleViewPromptTemplate = (template: PromptTemplate | null) => {
        setViewedPromptTemplate(template);
        setIsPromptViewerOpen(true);
    };

    const handleSlideRegenerate = (slidePosition: number) => {
        const slide = generatedSlides.find(s => s.position === slidePosition);
        if (slide) {
            setSelectedSlideForRegeneration(slide);
            setRegenerateModalOpen(true);
        }
    };

    const handleRegenerateSubmit = (request: FunnelSlideRegenerateRequest) => {
        slideRegenerateMutation.mutate({ slidePosition: request.slide_position, request });
        setRegenerateModalOpen(false);
    };

    const handleSlideView = (slide: FunnelSlide) => {
        if (slide.image_url) {
            openFullscreenImage(slide.image_url);
        }
    };

    const hasCompletedSlides = generatedSlides.some(slide => slide.status === 'completed');

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom>
                AI Funnel Generation
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Product Selection */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                1. Product & Reference Image
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Select product</InputLabel>
                                <Select
                                    value={selectedProductId}
                                    label="Select product"
                                    onChange={e => {
                                        setSelectedProductId(e.target.value as number);
                                        resetGeneration();
                                    }}
                                >
                                    {products?.map((p: Product) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.name} {p.brand ? `(${p.brand})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {selectedProduct && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Select Reference Image:
                                    </Typography>
                                    {(selectedProduct.images && selectedProduct.images.length > 0) ? (
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: 150, overflowY: 'auto', p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                            {selectedProduct.images.map((url) => (
                                                <Box
                                                    key={url}
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        cursor: 'pointer',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        border: `2px solid ${url === selectedReferenceImageUrl ? '#1976d2' : 'transparent'}`,
                                                        '&:hover': {
                                                            borderColor: url === selectedReferenceImageUrl ? '#1976d2' : '#90caf9',
                                                        },
                                                        transition: 'border-color 0.2s',
                                                    }}
                                                    onClick={() => setSelectedReferenceImageUrl(url)}
                                                >
                                                    <img
                                                        src={url}
                                                        alt="Product reference"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography color="text.secondary" variant="body2">
                                            This product has no images. Please upload images to the product first.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {selectedProductId && (
                                <Button
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                    onClick={() => setHistoryOpen(true)}
                                    size="small"
                                    disabled={isLoadingHistory}
                                >
                                    View History ({historyGenerations.length})
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Funnel Settings */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                2. Funnel Settings
                            </Typography>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Funnel Type</InputLabel>
                                        <Select
                                            value={funnelType}
                                            label="Funnel Type"
                                            onChange={e => setFunnelType(e.target.value as FunnelType)}
                                            disabled={generationStep !== 'idle'}
                                        >
                                            {FUNNEL_TYPES.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Marketplace</InputLabel>
                                        <Select
                                            value={targetMarketplace}
                                            label="Marketplace"
                                            onChange={e => setTargetMarketplace(e.target.value as string)}
                                            disabled={generationStep !== 'idle' || isLoadingMarketplaces}
                                        >
                                            {marketplaces.map((marketplace) => (
                                                <MenuItem key={marketplace.id} value={marketplace.code}>
                                                    {marketplace.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Slides Count</InputLabel>
                                <Select
                                    value={slidesCount}
                                    label="Slides Count"
                                    onChange={e => setSlidesCount(Number(e.target.value))}
                                    disabled={generationStep !== 'idle'}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                        <MenuItem key={n} value={n}>{n} slides</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>AI Service (Prompts)</InputLabel>
                                    <Select
                                        value={selectedAIServiceCode}
                                        label="AI Service (Prompts)"
                                        onChange={e => setSelectedAIServiceCode(e.target.value)}
                                        disabled={generationStep !== 'idle'}
                                    >
                                        {promptServices?.map((s: AIService) => (
                                            <MenuItem key={s.id} value={s.code}>
                                                {s.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedAIService && (
                                    <Tooltip title="View AI Service Details">
                                        <IconButton onClick={() => handleViewAIService(selectedAIService)} disabled={generationStep !== 'idle'}>
                                            <SearchIcon color="action" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>AI Service (Slides)</InputLabel>
                                    <Select
                                        value={selectedSlideServiceCode}
                                        label="AI Service (Slides)"
                                        onChange={e => setSelectedSlideServiceCode(e.target.value)}
                                        disabled={generationStep !== 'idle'}
                                    >
                                        {slideServices?.map((s: AIService) => (
                                            <MenuItem key={s.id} value={s.code}>
                                                {s.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedSlideService && (
                                    <Tooltip title="View AI Service Details">
                                        <IconButton onClick={() => handleViewAIService(selectedSlideService)} disabled={generationStep !== 'idle'}>
                                            <SearchIcon color="action" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Prompt Template</InputLabel>
                                    <Select
                                        value={selectedPromptTemplateId}
                                        label="Prompt Template"
                                        onChange={e => setSelectedPromptTemplateId(e.target.value as number)}
                                        disabled={generationStep !== 'idle'}
                                    >
                                        {promptTemplates?.map((t: PromptTemplate) => (
                                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedPromptTemplate && (
                                    <Tooltip title="View Prompt Template">
                                        <IconButton onClick={() => handleViewPromptTemplate(selectedPromptTemplate)} disabled={generationStep !== 'idle'}>
                                            <SearchIcon color="action" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            <TextField
                                multiline
                                minRows={2}
                                maxRows={4}
                                fullWidth
                                label="Additional Instructions (optional)"
                                value={additionalInstructions}
                                onChange={e => setAdditionalInstructions(e.target.value)}
                                disabled={generationStep !== 'idle'}
                                placeholder="e.g. focus on premium quality, highlight eco-friendly aspects"
                                sx={{ mb: 2 }}
                            />

                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                {/* Кнопка генерации промптов */}
                                <Button
                                    variant="contained"
                                    onClick={() => promptGenMutation.mutate()}
                                    disabled={!canStartPromptGen || promptGenMutation.isLoading}
                                >
                                    {promptGenMutation.isLoading || generationStep === 'prompt_generating'
                                        ? <CircularProgress size={24} />
                                        : 'Generate Funnel'}
                                </Button>

                                {/* ✅ ОБНОВЛЕННАЯ ЛОГИКА для кнопки генерации слайдов */}
                                <Button
                                    variant="contained"
                                    color={generationStep === 'slides_failed' ? 'warning' : 'primary'}
                                    onClick={() => slideGenMutation.mutate()}
                                    disabled={!canStartSlideGen || slideGenMutation.isLoading}
                                >
                                    {slideGenMutation.isLoading || generationStep === 'slides_generating'
                                        ? <CircularProgress size={24} />
                                        : generationStep === 'slides_failed'
                                            ? `Retry ${failedSlidesCount} Failed Slides`
                                            : 'Generate Slides'}
                                </Button>


                                <Button variant="text" color="secondary" onClick={resetAll}>
                                    Reset
                                </Button>
                            </Box>

                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {(generationStep === 'prompt_ready' || generationStep === 'slides_generating' || generationStep === 'completed') && (
                <Accordion expanded={promptsExpanded} onChange={() => setPromptsExpanded(!promptsExpanded)} sx={{ mb: 3}}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="funnel-prompts-content"
                        id="funnel-prompts-header"
                    >
                        <Typography variant="h6">
                            Generated Slide Prompts ({slidePrompts.length})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {slidePrompts.length === 0 && <Typography>No prompts available.</Typography>}
                        <Grid container spacing={2}>
                            {slidePrompts.map((prompt, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Slide {prompt.position}: {prompt.slide_type}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                                {prompt.prompt_text}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            )}

            {(generationStep === 'slides_generating' || generationStep === 'completed' || generationStep === 'failed' || generationStep === 'slides_failed') && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                Generated Funnel Slides
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                disabled={!hasCompletedSlides}
                            >
                                Download Funnel
                            </Button>
                        </Box>

                        <FunnelProgressIndicator
                            slides={generatedSlides}
                            totalSlides={slidesCount}
                            currentStep={generationStep}
                        />

                        <FunnelSlideViewer
                            slides={generatedSlides}
                            onSlideRegenerate={handleSlideRegenerate}
                            onSlideView={handleSlideView}
                        />
                    </CardContent>
                </Card>
            )}

            {currentGeneration && (currentGeneration.ai_raw_prompt || currentGeneration.ai_raw_response) && (
                <Accordion sx={{ mt: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Debug Info (Raw AI Data)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="overline">Raw Prompt sent to LLM</Typography>
                        <Paper variant="outlined" sx={{ p: 1, mb: 2, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {currentGeneration.ai_raw_prompt || 'Not available.'}
                        </Paper>
                        <Typography variant="overline">Raw Response from LLM</Typography>
                        <Paper variant="outlined" sx={{ p: 1, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {currentGeneration.ai_raw_response || 'Not available.'}
                        </Paper>
                    </AccordionDetails>
                </Accordion>
            )}

            <Dialog open={!!fullscreenImageUrl} onClose={closeFullscreenImage} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2 }}>
                    Slide Preview
                    <IconButton
                        aria-label="close"
                        onClick={closeFullscreenImage}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                    {fullscreenImageUrl && (
                        <img
                            src={fullscreenImageUrl}
                            alt="Full Screen Slide"
                            style={{
                                maxWidth: '100%',
                                maxHeight: 'calc(100vh - 120px)',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <FunnelHistoryDialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                historyGenerations={historyGenerations}
                product={selectedProduct}
                onLoadGeneration={handleHistoryLoad}
                isLoading={isLoadingHistory}
            />

            <FunnelSlideRegenerateModal
                open={regenerateModalOpen}
                onClose={() => setRegenerateModalOpen(false)}
                slide={selectedSlideForRegeneration}
                onRegenerate={handleRegenerateSubmit}
                isLoading={slideRegenerateMutation.isLoading}
            />

            <AIGuideDialog
                open={isAIGuideOpen}
                onClose={() => setIsAIGuideOpen(false)}
                aiService={viewedAIService}
            />

            <PromptViewerDialog
                open={isPromptViewerOpen}
                onClose={() => setIsPromptViewerOpen(false)}
                prompt={viewedPromptTemplate}
            />
        </Box>
    );
};

export default FunnelTestPage;